import { useEffect } from 'react';
import { InvoiceService } from 'api';
import { InvoiceClassQueryParams } from 'api/invoices/invoices.types';
import { AutocompletePaginationAsync } from 'components/Inputs';
import { InvoiceClass } from 'pages/invoices/invoices.types';
import { FieldPath, FieldValues } from 'react-hook-form';
import { createHandleOnBlur, handleChange } from 'utils/form.utils';
import { RenderProps } from './types';

type Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  formProps: RenderProps<TFieldValues, TName>;
  options: InvoiceClass[];
  params: InvoiceClassQueryParams;
  setParams: React.Dispatch<React.SetStateAction<InvoiceClassQueryParams>>;
  setOptions: React.Dispatch<React.SetStateAction<InvoiceClass[]>>;
  error?: boolean;
};

export function InvoiceClassAutocomplete<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  formProps,
  options,
  params,
  setOptions,
  setParams,
  error,
}: Props<TFieldValues, TName>) {
  const {
    getInvoiceClasses,
    loading: invoiceClassesLoading,
    invoiceClassesData,
  } = InvoiceService.useGetInvoiceClasses();

  useEffect(() => {
    if (invoiceClassesData?.length) {
      const enabledInvoiceClasses = invoiceClassesData.filter(
        (invoiceClass) => invoiceClass.enabled,
      );
      setOptions((prev) => [...prev, ...enabledInvoiceClasses]);
    }
  }, [invoiceClassesData]);

  useEffect(() => {
    getInvoiceClasses({ ...params });
  }, [getInvoiceClasses, params]);

  return (
    <AutocompletePaginationAsync
      {...formProps}
      width={280}
      hiddenLabel
      padding='dense'
      error={error}
      value={formProps.field.value}
      loading={invoiceClassesLoading}
      options={options}
      getOptionLabel={(option) => (option?.name ? option.name : '')}
      handleSearchChange={handleChange(setOptions, setParams)}
      onScrollBottom={() =>
        setParams((prev) => ({
          ...prev,
          pageNumber: (prev.pageNumber += 1),
        }))
      }
      onBlur={createHandleOnBlur(setOptions, setParams, formProps.field.onBlur)}
      onChange={(_, data) => formProps.field.onChange(data)}
    />
  );
}
